import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { createContractsUrl } from '@/src/constants/endpoints';
import { ContractsResponse } from '@/src/types/Response';
import { apiService } from '@/src/services/api-service';
import { mapResponseToClientShape } from '@/src/api/contracts/utils/mapResponseToClientShape';
import { GetCustomerContractsResponse } from '@/src/api/contracts/types';

type QueryCacheKey = `orion-contracts-${string}`;

const useContractsBaseQuery = <TData = GetCustomerContractsResponse>(
  accountId: string | null | undefined,
  options?: UseQueryOptions<
    GetCustomerContractsResponse,
    unknown,
    TData,
    QueryCacheKey
  >,
): UseQueryResult<TData> => {
  return useQuery(
    `orion-contracts-${accountId}` as QueryCacheKey,
    () =>
      apiService.get<GetCustomerContractsResponse>({
        url: createContractsUrl(accountId ?? ''),
        headers: {
          'orion-endpoint-version': '5',
        },
      }),
    {
      enabled: !!accountId,
      cacheTime: 30000,
      staleTime: 30000,
      ...options,
    },
  );
};

export const useContractsQuery = (
  accountId: string | null | undefined,
  options?: UseQueryOptions<
    GetCustomerContractsResponse,
    unknown,
    GetCustomerContractsResponse,
    QueryCacheKey
  >,
): UseQueryResult<GetCustomerContractsResponse> => {
  return useContractsBaseQuery(accountId, options);
};

/**
 * @deprecated This hook is deprecated and will be removed in a future release. Please use `useContractsQuery` instead.
 */
export const useContractsQueryLegacy = (
  accountId: string | null | undefined,
  options?: UseQueryOptions<
    GetCustomerContractsResponse,
    unknown,
    ContractsResponse,
    QueryCacheKey
  >,
): UseQueryResult<ContractsResponse> => {
  return useContractsBaseQuery<ContractsResponse>(accountId, {
    ...options,
    select: response => mapResponseToClientShape(response) as ContractsResponse,
  });
};
